import { Link } from 'react-router-dom'
import { useAgents } from '@/hooks/useAgents'
import { COUNCIL_BRANCH, GUARDIAN_BRANCH, type OrgBranch } from '@/lib/agent-org'
import type { AgentSummary } from '@/lib/api'

// ─── ExecutiveStrip ──────────────────────────────────────────────────────────
//
// Renders the DEC-021 dual-leadership structure as a thin header on the
// Command Centre home: Zeus (Council CEO, Business) and Athena (Guardian COO,
// Fleet Operations) as peers under Mark. Each card links through to the org
// chart and shows a live agent count.

function activeCount(branch: OrgBranch, agents: AgentSummary[]): number {
  const liveSkills = new Set(
    branch.groups
      .flatMap((g) => g.agents)
      .filter((a) => a.phase === 'live' || a.phase === 'infrastructure')
      .map((a) => a.skillName),
  )
  return agents.filter((a) => liveSkills.has(a.name) && a.status === 'active').length
}

function ExecCard({ branch, live, total }: { branch: OrgBranch; live: number; total: number }) {
  return (
    <Link
      to="/dashboard/agents"
      className="group relative block bg-cmd-container hover:bg-cmd-high transition-colors"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-0.5"
        style={{ backgroundColor: branch.accent }}
      />
      <div className="px-5 py-4 pl-6">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p
              className="font-label text-[0.6rem] tracking-widest uppercase"
              style={{ color: branch.accent }}
            >
              {branch.branch === 'council' ? 'Council CEO · Business' : 'Guardian COO · Fleet Ops'}
            </p>
            <h3 className="mt-1 font-sans text-xl font-semibold text-white tracking-tight">
              {branch.executive}
            </h3>
            <p className="font-label text-[0.65rem] text-cmd-outline mt-0.5">
              {branch.blurb}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono text-xl font-medium text-white tabular-nums leading-none">
              {live}
              <span className="text-cmd-outline text-sm">/{total}</span>
            </p>
            <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase mt-1">
              Active
            </p>
          </div>
        </div>
      </div>
    </Link>
  )
}

export function ExecutiveStrip() {
  const { data } = useAgents()
  const agents: AgentSummary[] = data?.data ?? []

  const councilLive = activeCount(COUNCIL_BRANCH, agents)
  const guardianLive = activeCount(GUARDIAN_BRANCH, agents)

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
          Leadership · DEC-021
        </span>
        <span className="font-mono text-[0.6rem] text-cmd-outline-variant">
          peers under Mark · two briefings · two decision queues
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <ExecCard branch={COUNCIL_BRANCH} live={councilLive} total={COUNCIL_BRANCH.totalAgents} />
        <ExecCard branch={GUARDIAN_BRANCH} live={guardianLive} total={GUARDIAN_BRANCH.totalAgents} />
      </div>
    </div>
  )
}
